import { useCallback, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Mic, MoreHorizontal, UserPlus } from 'lucide-react'
import { Screen } from '../components/Screen'
import { EmptyState, ErrorState, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { useMarkAttendance, useOccurrence, useSessionBoard, type MarkState } from '../data/hooks'
import { formatDayShort, isoDate, parseIsoDate, parseSessionKey } from '../data/schedule'
import type { AttendanceStatus } from '../data/types'
import { useOnline } from '../platform/network'
import type { RosterEntry } from '@shared/types/api'
import { CancelRestoreSheet, MoveSheet } from './OccurrenceSheets'

/**
 * Подписи без рода: пол по имени не угадываем, поэтому «Есть» и «Нет», а не
 * «Был» и «Была».
 */
export const STATUS_LABEL: Record<AttendanceStatus, string> = {
  present: 'Есть',
  absent: 'Нет',
  late: 'Опоздание',
  left_early: 'Ранний уход',
  sick: 'Болезнь',
  excused: 'Уважительная причина',
}
const ATTENDED: AttendanceStatus[] = ['present', 'late', 'left_early']
const MORE: AttendanceStatus[] = ['late', 'left_early', 'sick', 'excused']

type Tab = 'attendance' | 'plan' | 'summary'
type Action = 'menu' | 'cancel' | 'restore' | 'move'

/**
 * S05 «Занятие». Посещаемость сохраняется по тапу (решение владельца
 * 2026-10-01). «Не отмечено», «Есть» и «Нет» различаются; группа по умолчанию
 * присутствующей не считается. Действия с одним занятием (этап 3 шаг 10,
 * SC13): перенести, отменить, вернуть - постоянное расписание не меняется;
 * «Изменить расписание с этого дня» ведёт на правку расписания с датой.
 */
export function SessionScreen() {
  const { key = '' } = useParams()
  // Перенос открывает новое занятие на том же маршруте: экран начинается заново.
  return <SessionView key={key} sessionKey={key} />
}

function SessionView({ sessionKey: key }: { sessionKey: string }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { now } = useEnv()
  const parsed = parseSessionKey(key)
  const board = useSessionBoard(key)
  const marks = useMarkAttendance(key)
  const online = useOnline()
  const occurrence = useOccurrence(key)
  const [tab, setTab] = useState<Tab>('attendance')
  const [moreFor, setMoreFor] = useState<RosterEntry | null>(null)
  const [action, setAction] = useState<Action | null>(null)
  const today = isoDate(now())

  // Назад - туда, откуда пришли (расписание с его датой и режимом). Если
  // экран открыт по ссылке, истории нет - ведём на «Сегодня».
  const back = useCallback(() => {
    if (location.key !== 'default') navigate(-1)
    else navigate('/today', { replace: true })
  }, [location.key, navigate])

  const date = parsed ? parseIsoDate(parsed.date) : null
  const subtitle =
    parsed && date
      ? `Тренировка / ${formatDayShort(date)}${parsed.startTime ? ` / ${parsed.startTime}` : ''}`
      : undefined
  const title = board.data?.group_name ?? (board.isError ? 'Занятие' : ' ')
  // Подпись для Штаба: «Стрижи, тренировка 1 октября, 17:00» - уходит в вопрос как есть
  const hubContext =
    board.data && parsed && date
      ? {
          label: `${board.data.group_name}, тренировка ${formatDayShort(date)}${parsed.startTime ? `, ${parsed.startTime}` : ''}`,
          title: board.data.group_name,
          detail: `Тренировка ${formatDayShort(date)}${parsed.startTime ? `, ${parsed.startTime}` : ''}`,
          ref: { kind: 'session' as const, groupId: parsed.groupId, date: parsed.date, startTime: parsed.startTime },
        }
      : undefined

  if (!parsed) {
    return (
      <Screen title="Занятие не найдено" back={back}>
        <EmptyState title="Ссылка на занятие устарела или неверна" text="Откройте занятие из расписания." />
      </Screen>
    )
  }

  const cancelled = !!board.data?.cancelled
  const upcoming = parsed.date >= today

  return (
    <Screen
      title={title}
      subtitle={subtitle}
      back={back}
      hubContext={hubContext}
      headerAction={
        board.data ? (
          <button type="button" className="pc-icon-btn" aria-label="Действия занятия" onClick={() => setAction('menu')}>
            <MoreHorizontal size={20} aria-hidden="true" />
          </button>
        ) : undefined
      }
    >
      <div className="pc-segments" role="tablist" aria-label="Разделы занятия" style={{ marginTop: 16 }}>
        {(
          [
            ['attendance', 'Посещаемость'],
            ['plan', 'План'],
            ['summary', 'Итоги'],
          ] as const
        ).map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}>
            {label}
          </button>
        ))}
      </div>

      {tab !== 'attendance' ? (
        <EmptyState title="Появится на следующем этапе" text="В срезе редизайна готова только посещаемость." />
      ) : board.isPending ? (
        <Skeleton rows={4} label="Загружаю состав группы" />
      ) : board.isError ? (
        <ErrorState title="Не удалось открыть занятие" onRetry={() => void board.refetch()} />
      ) : board.data.cancelled ? (
        <EmptyState
          title="Занятие отменено"
          text="Отметки для отменённого занятия не ставятся. Отменено только это занятие, расписание группы прежнее."
          action={
            <button type="button" className="pc-btn" onClick={() => setAction('restore')}>
              Вернуть занятие
            </button>
          }
        />
      ) : board.data.roster.length === 0 ? (
        <EmptyState
          title="В группе пока нет спортсменов"
          text="Добавьте спортсменов в группу, и они появятся здесь. Пришёл новичок - добавьте его прямо с занятия."
          action={
            <button type="button" className="pc-btn" onClick={() => navigate(`/sessions/${encodeURIComponent(key)}/newcomer`)}>
              <UserPlus size={18} aria-hidden="true" />
              Добавить новичка
            </button>
          }
        />
      ) : (
        <>
          <div className="pc-roll-summary">
            <strong>
              Отмечено {board.data.marked_count} из {board.data.total_count}
            </strong>
            <span className="pc-muted" style={{ fontSize: 14 }}>
              есть: {board.data.present_count}
            </span>
          </div>
          <p className="pc-muted" style={{ margin: '4px 0 0', fontSize: 14 }}>
            Неотмеченный не считается отсутствующим. Отметка сохраняется сразу.
          </p>

          {/* Перекличка фразой и новичок (этап 5, шаг 2) */}
          <div className="pc-session-tools">
            <button type="button" className="pc-btn" onClick={() => navigate(`/sessions/${encodeURIComponent(key)}/roll-call`)}>
              <Mic size={18} aria-hidden="true" />
              Перекличка
            </button>
            <button type="button" className="pc-btn" onClick={() => navigate(`/sessions/${encodeURIComponent(key)}/newcomer`)}>
              <UserPlus size={18} aria-hidden="true" />
              Добавить новичка
            </button>
          </div>

          <div aria-live="polite" className="pc-visually-hidden">
            {marks.saving > 0 ? `Сохраняю отметок: ${marks.saving}` : ''}
          </div>
          {marks.failed > 0 && (
            <div className="pc-alert" role="alert">
              <span style={{ flex: 1 }}>
                {online ? 'Не сохранено отметок' : 'Нет сети - не сохранено отметок'}: {marks.failed}
              </span>
              <button type="button" className="pc-btn" onClick={marks.retryFailed}>
                Повторить
              </button>
            </div>
          )}

          <ul className="pc-list" style={{ marginTop: 8 }}>
            {board.data.roster.map((r) => (
              <li key={r.athlete_id}>
                <RosterRow
                  entry={r}
                  state={marks.states[r.athlete_id]}
                  onMark={(s) => void marks.mark(r.athlete_id, s)}
                  onMore={() => setMoreFor(r)}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {action === 'menu' && (
        <Sheet title="Занятие" onClose={() => setAction(null)}>
          <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
            Перенос и отмена касаются только этого занятия: постоянное расписание группы не меняется.
          </p>
          {!cancelled && upcoming && (
            <button type="button" className="pc-btn pc-btn--block" onClick={() => setAction('move')}>
              Перенести занятие
            </button>
          )}
          {!cancelled && (
            <button type="button" className="pc-btn pc-btn--block" onClick={() => setAction('cancel')}>
              Отменить занятие
            </button>
          )}
          {cancelled && (
            <button type="button" className="pc-btn pc-btn--block" onClick={() => setAction('restore')}>
              Вернуть занятие
            </button>
          )}
          {upcoming && (
            <button
              type="button"
              className="pc-btn pc-btn--block"
              onClick={() => navigate(`/groups/${parsed.groupId}/schedule?from=${parsed.date}`)}
            >
              Изменить расписание с этого дня
            </button>
          )}
          {upcoming && (
            <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 13 }}>
              «Изменить расписание с этого дня» меняет это и все следующие занятия группы.
            </p>
          )}
        </Sheet>
      )}
      {(action === 'cancel' || action === 'restore') && (
        <CancelRestoreSheet kind={action} occurrence={occurrence} onClose={() => setAction(null)} />
      )}
      {action === 'move' && (
        <MoveSheet
          sessionKey={key}
          occurrence={occurrence}
          today={today}
          onClose={() => setAction(null)}
          onMoved={(toKey) => {
            setAction(null)
            navigate(`/sessions/${encodeURIComponent(toKey)}`, { replace: true })
          }}
        />
      )}

      {moreFor && (
        <Sheet title={moreFor.name} onClose={() => setMoreFor(null)}>
          <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
            Другие отметки
          </p>
          {MORE.map((s) => (
            <button
              key={s}
              type="button"
              className={`pc-btn pc-btn--block${moreFor.status === s ? ' pc-btn--primary' : ''}`}
              onClick={() => {
                void marks.mark(moreFor.athlete_id, s)
                setMoreFor(null)
              }}
            >
              {STATUS_LABEL[s]}
            </button>
          ))}
          {moreFor.status && (
            <button
              type="button"
              className="pc-btn pc-btn--block"
              onClick={() => {
                void marks.mark(moreFor.athlete_id, null)
                setMoreFor(null)
              }}
            >
              Снять отметку
            </button>
          )}
        </Sheet>
      )}
    </Screen>
  )
}

function RosterRow({
  entry,
  state,
  onMark,
  onMore,
}: {
  entry: RosterEntry
  state?: MarkState
  onMark: (s: AttendanceStatus | null) => void
  onMore: () => void
}) {
  const online = useOnline()
  const saving = state?.phase === 'saving'
  const failed = state?.phase === 'error'
  // Пока ответа нет, показываем выбор пунктиром, но статус берём с сервера.
  const shown: AttendanceStatus | null | undefined = saving || failed ? state.status : entry.status
  const attended = shown ? ATTENDED.includes(shown) : false
  const missed = shown ? !attended : false

  let caption = entry.status ? STATUS_LABEL[entry.status] : 'Не отмечено'
  if (entry.paused) caption = `На паузе / ${caption}`
  // Цвет только у сбоя: зелёный «Нет» читался бы как хороший исход.
  let tone: 'muted' | 'error' = 'muted'
  if (saving) caption = 'Сохраняю…'
  if (failed) {
    caption = online ? 'Не сохранено' : 'Нет сети - не сохранено'
    tone = 'error'
  }

  return (
    <div className="pc-roll-row" data-paused={entry.paused || undefined}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="pc-roll-row__name">{entry.name}</div>
        <div className="pc-roll-row__state" data-tone={tone}>
          {caption}
          {failed && (
            <>
              {' · '}
              <button
                type="button"
                className="pc-btn pc-btn--link"
                style={{ fontSize: 13 }}
                onClick={() => onMark(state.status)}
              >
                Повторить
              </button>
            </>
          )}
        </div>
      </div>
      <div className="pc-mark" role="group" aria-label={`Отметка: ${entry.name}`}>
        <button
          type="button"
          data-kind="yes"
          aria-pressed={attended}
          data-pending={(saving || failed) && attended}
          disabled={saving}
          onClick={() => onMark('present')}
        >
          Есть
        </button>
        <button
          type="button"
          data-kind="no"
          aria-pressed={missed}
          data-pending={(saving || failed) && missed}
          disabled={saving}
          onClick={() => onMark('absent')}
        >
          Нет
        </button>
        <button type="button" className="pc-mark__more" aria-label="Другие отметки" disabled={saving} onClick={onMore}>
          <MoreHorizontal size={20} aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
